import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import AppZipFileInput from '@tenant/core/AppZipFileInput';
import {
  APP_CONNECTIVITY_STATE_ENUM,
  APP_TYPE_ENUM,
  MOBILE_APP_TYPE_ENUM_SELECT,
} from '@tenant/utility/constants/enum';
import { getBlobFileImage } from 'pages/publishApp/helpers/common';
import { memo, useEffect, useMemo } from 'react';
import FeatureStepMobileMarketPlace from './FeatureStepMobileMarketPlace';
import AppGridContainer from '@tenant/core/AppGridContainer';
import AppGlossary from '@tenant/core/App/Common/AppGlossary';
import { generateUUID } from '@tenant/utility/helper/Utils';
import checkIsModuleMode from '../../../../../helpers/checkIsModuleMode';
import PropTypes from 'prop-types';

const MobileStep = ({
  values,
  errors,
  setFieldValue,
  resetMobileFieldsIfNeeded = () => {},
}) => {
  const showConnectivityState = useMemo(() => {
    return values.MobileIntegrationMode !== APP_TYPE_ENUM['Modular'];
  }, [values.MobileIntegrationMode]);

  const showBundleZipFile = useMemo(() => {
    const isOfflineState =
      values.AppConnectivityState === APP_CONNECTIVITY_STATE_ENUM['Offline'];
    return showConnectivityState && isOfflineState;
  }, [values.AppConnectivityState]);

  const showMobileAppUrl = useMemo(() => {
    const isOnlineState =
      values.AppConnectivityState === APP_CONNECTIVITY_STATE_ENUM['Online'];
    return showConnectivityState && isOnlineState;
  }, [values.AppConnectivityState]);

  const defaultMobileAppCode = useMemo(() => generateUUID(), []);

  // Auto generate mobile app code in case new app created
  useEffect(() => {
    if (!values.AppId && !values.MobileAppCode) {
      setFieldValue('MobileAppCode', defaultMobileAppCode);
    }
  }, [values.AppId, values.MobileAppCode]);

  return (
    <>
      <Grid container item xs={12} direction='row' spacing={2} mb={4}>
        <Grid item xs={12} sm={6} md={6}>
          <Box component='p' color='text.primary' fontSize={14} mb={2}>
            Mobile Integration Mode
            <Box component='span' ml={1} sx={{ color: 'error.main' }}>
              *
            </Box>
            <AppGlossary title='Select how your app will integrate with the Joblogic Mobile App' />
          </Box>
          <FormControl
            fullWidth
            error={Boolean(errors.MobileIntegrationMode)}
            sx={{ '& .MuiFormHelperText-root': { display: 'none' } }}
          >
            <Select
              value={values.MobileIntegrationMode}
              onChange={(e) => {
                setFieldValue('MobileIntegrationMode', e.target.value);
                resetMobileFieldsIfNeeded(e.target.value);
              }}
            >
              {MOBILE_APP_TYPE_ENUM_SELECT.map((item) => (
                <MenuItem
                  key={item.value}
                  value={item.value}
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                    whiteSpace: 'pre',
                    p: 2,
                  }}
                >
                  <Tooltip title={item.description}>
                    <Box>
                      {item.label}
                      <FormHelperText
                        display='block'
                        sx={{
                          fontSize: 10,
                          whiteSpace: 'normal',
                          overflowWrap: 'break-word',
                          maxWidth: 475,
                        }}
                      >
                        {item.description}
                      </FormHelperText>
                    </Box>
                  </Tooltip>
                </MenuItem>
              ))}
            </Select>
            {Boolean(errors.MobileIntegrationMode) && (
              <FormHelperText>{errors.MobileIntegrationMode}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box component='p' color='text.primary' fontSize={14} mb={2}>
            Mobile App Code
            <Box component='span' ml={1} sx={{ color: 'error.main' }}>
              *
            </Box>
            <AppGlossary title='A unique identifier for the app, used to distinguish it from others. This code should not be changed unless absolutely necessary.' />
          </Box>
          <TextField
            placeholder='Enter text'
            error={!!errors.MobileAppCode}
            helperText={errors.MobileAppCode}
            value={values.MobileAppCode ?? ''}
            onChange={async (e) => {
              setFieldValue('MobileAppCode', e.target.value);
            }}
            onBlur={(e) => {
              setFieldValue('MobileAppCode', values.MobileAppCode?.trim());
            }}
            fullWidth
          />
        </Grid>
      </Grid>

      {/* App Connectivity State */}
      {showConnectivityState && (
        <Grid item xs={12} sm={12} mb={4}>
          <Box component='p' color='text.primary' fontSize={14} mb={2}>
            App Connectivity State
            <Box component='span' ml={1} sx={{ color: 'error.main' }}>
              *
            </Box>
            <AppGlossary title='Defines the app connectivity mode. Online loads the app from the Hosted Mobile App Address, while Offline uses a .zip file.' />
          </Box>
          <FormControl
            disabled={checkIsModuleMode(values.AppConnectivityState)}
          >
            <RadioGroup row>
              <FormControlLabel
                checked={
                  values.AppConnectivityState ===
                  APP_CONNECTIVITY_STATE_ENUM['Online']
                }
                value={APP_CONNECTIVITY_STATE_ENUM['Online']}
                control={<Radio />}
                label='Online'
                onChange={() =>
                  setFieldValue(
                    'AppConnectivityState',
                    APP_CONNECTIVITY_STATE_ENUM['Online'],
                  )
                }
              />
              <FormControlLabel
                checked={
                  values.AppConnectivityState ===
                  APP_CONNECTIVITY_STATE_ENUM['Offline']
                }
                value={APP_CONNECTIVITY_STATE_ENUM['Offline']}
                control={<Radio />}
                label='Offline'
                onChange={() =>
                  setFieldValue(
                    'AppConnectivityState',
                    APP_CONNECTIVITY_STATE_ENUM['Offline'],
                  )
                }
              />
            </RadioGroup>
          </FormControl>
          {Boolean(errors.AppConnectivityState) && (
            <FormHelperText sx={{ color: 'error.main' }}>
              {errors.AppConnectivityState}
            </FormHelperText>
          )}
        </Grid>
      )}

      {/* Bundle Zip File in case app Offline*/}
      {showBundleZipFile && (
        <Grid
          item
          xs={12}
          sx={{
            mt: 2,
            mb: 5,
          }}
        >
          <Box component='p' color='text.primary' fontSize={14} mb={2}>
            Bundle zip file
            <Box component='span' ml={1} sx={{ color: 'error.main' }}>
              *
            </Box>
            <AppGlossary title='Contains all the app HTML, CSS, and JavaScript files, used to load the app in a webview when in Offline mode.' />
          </Box>

          <AppZipFileInput
            defaultFile={values.BundleZipFile}
            error={!!errors.BundleZipBlobReference}
            helperText={errors.BundleZipBlobReference}
            appUrl={
              values.AppConnectivityState ===
                APP_CONNECTIVITY_STATE_ENUM['Offline'] && values.MobileAppUrl
                ? values.MobileAppUrl
                : null
            }
            onChange={(file) => {
              if (file) {
                getBlobFileImage(file, (blob) => {
                  setFieldValue('BundleZipBlobReference', blob);
                });
              } else {
                setFieldValue('BundleZipBlobReference', null);
              }
              setFieldValue('BundleZipFile', file);
            }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        {/* Mobile app URL in case app Online, but we temporary saved it into MobileFeatureUrl first*/}
        {showMobileAppUrl && (
          <Box mb={5}>
            <Box component='p' color='text.primary' fontSize={14} mb={2}>
              Hosted Mobile App Address
              <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                *
              </Box>
              <AppGlossary title='The URL to load the app in a webview when the app is in Online mode.' />
            </Box>
            <TextField
              placeholder='Enter URL'
              error={!!errors.MobileFeatureUrl}
              helperText={errors.MobileFeatureUrl}
              value={values.MobileFeatureUrl ?? ''}
              onChange={async (e) => {
                setFieldValue('MobileFeatureUrl', e.target.value);
              }}
              onBlur={(e) => {
                setFieldValue(
                  'MobileFeatureUrl',
                  values.MobileFeatureUrl?.trim(),
                );
              }}
              fullWidth
            />
          </Box>
        )}
      </Grid>

      {values.MobileIntegrationMode === APP_TYPE_ENUM['Embedded'] && (
        <AppGridContainer sx={{ mb: 2 }}>
          <FeatureStepMobileMarketPlace
            values={values}
            errors={errors}
            setFieldValue={setFieldValue}
          />
        </AppGridContainer>
      )}
    </>
  );
};

export default memo(MobileStep);

MobileStep.propTypes = {
  values: PropTypes.object,
  errors: PropTypes.object,
  setFieldValue: PropTypes.func,
  resetMobileFieldsIfNeeded: PropTypes.func,
};
